import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  LayoutDashboard, BookOpen, Puzzle, Brain, FileText,
  Users, BarChart3, LogOut, Plus, Settings
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if admin is logged in
    if (!localStorage.getItem('adminAuth')) {
      navigate('/admin');
      return;
    }
    fetchStats();
  }, [navigate]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    navigate('/admin');
  };

  const contentCards = [
    { title: 'Quiz Content', icon: Brain, color: 'from-purple-500 to-pink-500', link: '/admin/quizzes', count: stats?.total_content?.quizzes || 0 },
    { title: 'Memory Games', icon: Puzzle, color: 'from-blue-500 to-cyan-500', link: '/admin/memory', count: stats?.total_content?.memories || 0 },
    { title: 'Stories', icon: BookOpen, color: 'from-green-500 to-emerald-500', link: '/admin/stories', count: stats?.total_content?.stories || 0 },
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <LayoutDashboard className="w-8 h-8" />
              <div>
                <h1 className="text-2xl font-bold">Admin Dashboard</h1>
                <p className="text-blue-100">Little Genius Hub Content Management</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-white text-white hover:bg-white/20"
            >
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="p-6 bg-white shadow-lg" style={{ borderRadius: '16px' }}>
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-purple-100">
                <Brain className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-gray-500 text-sm">Total Quizzes</p>
                <p className="text-2xl font-bold">{stats?.total_content?.quizzes || 0}</p>
              </div>
            </div>
          </Card>
          <Card className="p-6 bg-white shadow-lg" style={{ borderRadius: '16px' }}>
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-blue-100">
                <Puzzle className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-gray-500 text-sm">Memory Games</p>
                <p className="text-2xl font-bold">{stats?.total_content?.memories || 0}</p>
              </div>
            </div>
          </Card>
          <Card className="p-6 bg-white shadow-lg" style={{ borderRadius: '16px' }}>
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-green-100">
                <BookOpen className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-gray-500 text-sm">Stories</p>
                <p className="text-2xl font-bold">{stats?.total_content?.stories || 0}</p>
              </div>
            </div>
          </Card>
          <Card className="p-6 bg-white shadow-lg" style={{ borderRadius: '16px' }}>
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-orange-100">
                <Users className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-gray-500 text-sm">Total Players</p>
                <p className="text-2xl font-bold">{stats?.total_players || 0}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Content Management Cards */}
        <h2 className="text-xl font-bold text-gray-800 mb-4">Content Management</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {contentCards.map((card, index) => (
            <Link key={index} to={card.link}>
              <Card className="p-6 bg-white shadow-lg hover:shadow-xl transition-all hover:scale-105 cursor-pointer" style={{ borderRadius: '16px' }}>
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${card.color} flex items-center justify-center mb-4`}>
                  <card.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">{card.title}</h3>
                <p className="text-gray-500">{card.count} items</p>
                <div className="mt-4 flex items-center text-blue-600 font-semibold">
                  <Plus className="w-4 h-4 mr-1" /> Manage Content
                </div>
              </Card>
            </Link>
          ))}
        </div>

        {/* Content by Age Group */}
        <h2 className="text-xl font-bold text-gray-800 mb-4">Content by Age Group</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {stats?.content_by_age && Object.entries(stats.content_by_age).map(([age, data]) => (
            <Card key={age} className="p-6 bg-white shadow-lg" style={{ borderRadius: '16px' }}>
              <h3 className="text-lg font-bold text-gray-800 capitalize mb-4">
                {age === 'preschool' ? '🌟 Preschool (3-5)' : age === 'elementary' ? '📚 Elementary (6-8)' : '🎓 Advanced (9-12)'}
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Quizzes</span>
                  <span className="font-semibold">{data.quizzes}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Memory Games</span>
                  <span className="font-semibold">{data.memories}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Stories</span>
                  <span className="font-semibold">{data.stories}</span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
